/**
 * The `/campaigns` experiment readout's one number (email-newsletter-editions,
 * 2026-10-18): the chance that arm B's true rate is above arm A's, from the
 * two arms' counts. Pure; `ab-test.test.ts` pins it.
 *
 * Each arm's rate starts from a uniform prior, Beta(1, 1), so after `s`
 * successes in `n` trials it is Beta(1 + s, 1 + n − s). For two Betas whose
 * first parameter is a whole number, P(p_B > p_A) has a closed form (Evan
 * Miller, "Formulas for Bayesian A/B Testing"):
 *
 *   P = Σ_{i=0}^{α_B − 1}  B(α_A + i, β_A + β_B)
 *                         / ((β_B + i) · B(1 + i, β_B) · B(α_A, β_A))
 *
 * B is the beta function, worked in logs (the betas of a few hundred
 * trials underflow a double). The sum runs over whichever arm has fewer
 * successes, using P(p_B > p_A) = 1 − P(p_A > p_B): a tie has probability
 * zero. No sampling, so the same counts always give the same number.
 */

/** One arm's counts: `successes` of `trials` (e.g. unique clicks of delivered). */
export interface ArmCounts {
  readonly successes: number;
  readonly trials: number;
}

// Lanczos approximation, g = 7, n = 9 — about 15 significant digits for x ≥ 0.5.
const LANCZOS_G = 7;
const LANCZOS = [
  0.99999999999980993, 676.5203681218851, -1259.1392167224028, 771.32342877765313,
  -176.61502916214059, 12.507343278686905, -0.13857109526572012, 9.9843695780195716e-6,
  1.5056327351493116e-7,
] as const;

/** ln Γ(x) for x ≥ 0.5 — every argument here is at least 1. */
export function lnGamma(x: number): number {
  const z = x - 1;
  let sum = LANCZOS[0];
  for (let i = 1; i < LANCZOS.length; i += 1) sum += (LANCZOS[i] ?? 0) / (z + i);
  const t = z + LANCZOS_G + 0.5;
  return 0.5 * Math.log(2 * Math.PI) + (z + 0.5) * Math.log(t) - t + Math.log(sum);
}

/** ln B(a, b). */
export function lnBeta(a: number, b: number): number {
  return lnGamma(a) + lnGamma(b) - lnGamma(a + b);
}

/** P(X_B > X_A) for X_A ~ Beta(aA, bA), X_B ~ Beta(aB, bB), aB a whole number. */
function chanceSecondAbove(aA: number, bA: number, aB: number, bB: number): number {
  const lnBetaA = lnBeta(aA, bA);
  let total = 0;
  for (let i = 0; i < aB; i += 1) {
    total += Math.exp(lnBeta(aA + i, bA + bB) - Math.log(bB + i) - lnBeta(1 + i, bB) - lnBetaA);
  }
  return total;
}

function validArm(a: ArmCounts): boolean {
  return (
    Number.isInteger(a.successes) &&
    Number.isInteger(a.trials) &&
    a.successes >= 0 &&
    a.trials >= a.successes
  );
}

/**
 * The chance that B's true rate is above A's, between 0 and 1; `null` for
 * counts that are not whole, are negative, or have more successes than
 * trials. Two arms with no trials at all are a coin toss: 0.5.
 */
export function chanceBAboveA(a: ArmCounts, b: ArmCounts): number | null {
  if (!validArm(a) || !validArm(b)) return null;
  const aA = 1 + a.successes;
  const bA = 1 + a.trials - a.successes;
  const aB = 1 + b.successes;
  const bB = 1 + b.trials - b.successes;
  const p = aB <= aA ? chanceSecondAbove(aA, bA, aB, bB) : 1 - chanceSecondAbove(aB, bB, aA, bA);
  return Math.min(1, Math.max(0, p));
}
